import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import taapService from '../../services/api/taapService';
import { TaapAccount, TaapProduct } from '../../types/taap';

const parseId = (value: string | null): number | null =>
  value && /^[1-9]\d*$/.test(value) ? Number(value) : null;

export interface TaapProductScope {
  /** 商品一覧を読み終えたか（失敗も含む）。 */
  loaded: boolean;
  loadFailed: boolean;
  products: TaapProduct[];
  product: TaapProduct | null;
  productId: number | null;
  accountId: number | null;
  /** 選択中の商品に紐付いたアカウント。名前は /taap/accounts から引く。 */
  productAccounts: TaapAccount[];
  selectProduct: (id: number) => void;
  selectAccount: (id: number | null) => void;
  /**
   * API が 404 を返したとき（商品の紐付けが外れた・URL のアカウントが商品に無い等）に
   * 先頭の商品へ戻す。既に先頭の商品を絞り込みなしで見ているなら戻す先が無いので false。
   * failed は 404 を返したリクエストの範囲。その間に利用者が選び直していたら書き換えない。
   */
  recoverToFirstProduct: (failed: { productId: number | null; accountId: number | null }) => Promise<boolean>;
}

/**
 * TAAP の「どの商品の数字を見るか」。URL の ?product / ?account を正本にする。
 *
 * 組織全体の合算は出さないので、商品が決まるまで数字は取りに行かない。
 * ?product が無い・組織に無いときは、?account を含む商品（Meta / Google 画面から
 * アカウント付きで来た場合）か先頭の商品に寄せて URL も書き換える。
 */
export const useTaapProductScope = (): TaapProductScope => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<TaapProduct[]>([]);
  const [accounts, setAccounts] = useState<TaapAccount[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const productParam = parseId(searchParams.get('product'));
  const accountParam = parseId(searchParams.get('account'));

  useEffect(() => {
    let active = true;
    taapService
      .getProducts()
      .then((next) => {
        if (active) setProducts(next);
      })
      .catch((e) => {
        console.error('TAAP products load failed:', e);
        if (active) setLoadFailed(true);
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    taapService
      .getAccounts()
      .then((next) => {
        if (active) setAccounts(next);
      })
      .catch((e) => console.error('TAAP accounts load failed:', e));
    return () => {
      active = false;
    };
  }, []);

  const resolved = useMemo((): { productId: number | null; accountId: number | null } => {
    if (products.length === 0) return { productId: null, accountId: null };
    if (productParam !== null && products.some((product) => product.id === productParam)) {
      // 商品に無いアカウントもそのまま送る。API が 404 を返し、先頭の商品に戻す。
      return { productId: productParam, accountId: accountParam };
    }
    const owner = accountParam !== null
      ? products.find((product) => product.ad_account_ids.includes(accountParam))
      : undefined;
    return owner
      ? { productId: owner.id, accountId: accountParam }
      : { productId: products[0].id, accountId: null };
  }, [products, productParam, accountParam]);

  const writeScope = useCallback(
    (productId: number | null, accountId: number | null) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (productId === null) next.delete('product');
          else next.set('product', String(productId));
          if (accountId === null) next.delete('account');
          else next.set('account', String(accountId));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // 寄せた先を URL に残す。タブ移動・共有・リロードで同じ商品が開くように。
  useEffect(() => {
    if (resolved.productId === null) return;
    if (resolved.productId !== productParam || resolved.accountId !== accountParam) {
      writeScope(resolved.productId, resolved.accountId);
    }
  }, [resolved, productParam, accountParam, writeScope]);

  const product = useMemo(
    () => products.find((candidate) => candidate.id === resolved.productId) ?? null,
    [products, resolved.productId],
  );

  const productAccounts = useMemo((): TaapAccount[] => {
    if (!product) return [];
    return product.ad_account_ids.map((id) => {
      const found = accounts.find((account) => account.id === id);
      return found ?? { id, platform: product.platforms[0] ?? 'meta', account_name: null, status: '', last_synced_at: null };
    });
  }, [product, accounts]);

  const selectProduct = useCallback((id: number) => writeScope(id, null), [writeScope]);
  const selectAccount = useCallback(
    (id: number | null) => writeScope(resolved.productId, id),
    [writeScope, resolved.productId],
  );

  // setSearchParams は URL が変わるたびに作り直される。読み込みの依存に入るので、
  // 最新値は ref から読んで関数の参照を固定する（URL の正規化で二重に読み込まないように）。
  const latest = useRef({ products, resolved, writeScope });
  latest.current = { products, resolved, writeScope };

  const recoverToFirstProduct = useCallback(async (failed: { productId: number | null; accountId: number | null }) => {
    // 商品そのものが消えている場合に備えて一覧を取り直す。
    const fresh = await taapService.getProducts().catch(() => latest.current.products);
    const current = latest.current.resolved;
    // 一覧を待つ間に別の商品・アカウントが選ばれていたら、その選択を優先する。
    // 呼び出し側は自分のリクエストが古くなったことを見て何も表示しない。
    if (current.productId !== failed.productId || current.accountId !== failed.accountId) return true;
    setProducts(fresh);
    const first = fresh[0]?.id ?? null;
    if (first !== null && first === failed.productId && failed.accountId === null) return false;
    latest.current.writeScope(first, null);
    return true;
  }, []);

  return {
    loaded,
    loadFailed,
    products,
    product,
    productId: resolved.productId,
    accountId: resolved.accountId,
    productAccounts,
    selectProduct,
    selectAccount,
    recoverToFirstProduct,
  };
};
